'use client';

import { useActionState, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Button } from '@/components/button';
import { cn } from '@/lib/utils';
import { Select } from '@/components/select';
import { submitContactInquiry } from '@/app/actions/contact';
import {
  contactTopics,
  parseContactTopicParam,
  type ContactTopic,
} from '@/lib/contact/contact-inquiry';

const topicLabels: Record<ContactTopic, string> = {
  sales: 'Sales',
  enterprise: 'Enterprise',
  support: 'Support',
  other: 'General inquiry',
};

const inputClassName =
  'w-full rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] px-4 py-3 text-sm text-[rgb(var(--foreground))] placeholder:text-[rgb(var(--foreground-tertiary))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--primary))]';

export function ContactForm() {
  const searchParams = useSearchParams();
  const defaultTopic = parseContactTopicParam(searchParams.get('topic'));
  const [topic, setTopic] = useState<ContactTopic>(defaultTopic);

  const [state, formAction, isPending] = useActionState(submitContactInquiry, null);

  useEffect(() => {
    setTopic(defaultTopic);
  }, [defaultTopic]);

  if (state?.success) {
    return (
      <div
        className="rounded-2xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-8 text-center"
        role="status"
      >
        <h2 className="text-xl font-semibold text-[rgb(var(--foreground))] mb-2">Message sent</h2>
        <p className="text-[rgb(var(--foreground-secondary))]">
          Thanks for reaching out. We&apos;ll get back to you at the email you provided.
        </p>
      </div>
    );
  }

  const fieldErrors = state?.success === false ? state.fieldErrors : undefined;

  // SAFETY: The surrounding typed contract establishes the asserted representation.
  return (
    <form action={formAction} className="space-y-6">
      <input
        type="hidden"
        name="pageUrl"
        value={typeof window !== 'undefined' ? window.location.href : ''}
      />
      {/* Honeypot */}
      <div className="absolute -left-[9999px] opacity-0" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      {state?.success === false && state.error && (
        <p className="rounded-lg border border-[rgb(var(--error)/0.4)] bg-[rgb(var(--error)/0.08)] px-4 py-3 text-sm text-[rgb(var(--error))]">
          {state.error}
        </p>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label
            htmlFor="name"
            className="mb-2 block text-sm font-medium text-[rgb(var(--foreground))]"
          >
            Name
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            autoComplete="name"
            className={inputClassName}
            aria-invalid={Boolean(fieldErrors?.name)}
          />
          {fieldErrors?.name?.[0] && (
            <p className="mt-1 text-xs text-[rgb(var(--error))]">{fieldErrors.name[0]}</p>
          )}
        </div>
        <div>
          <label
            htmlFor="email"
            className="mb-2 block text-sm font-medium text-[rgb(var(--foreground))]"
          >
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            className={inputClassName}
            aria-invalid={Boolean(fieldErrors?.email)}
          />
          {fieldErrors?.email?.[0] && (
            <p className="mt-1 text-xs text-[rgb(var(--error))]">{fieldErrors.email[0]}</p>
          )}
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label
            htmlFor="company"
            className="mb-2 block text-sm font-medium text-[rgb(var(--foreground))]"
          >
            Company <span className="text-[rgb(var(--foreground-tertiary))]">(optional)</span>
          </label>
          <input
            id="company"
            name="company"
            type="text"
            autoComplete="organization"
            className={inputClassName}
          />
        </div>
        <div>
          <label
            htmlFor="topic"
            className="mb-2 block text-sm font-medium text-[rgb(var(--foreground))]"
          >
            Topic
          </label>
          <Select
            id="topic"
            name="topic"
            value={topic}
            onChange={(value) => setTopic(value as ContactTopic)}
            options={contactTopics.map((value) => ({ value, label: topicLabels[value] }))}
            ariaLabel="Topic"
            className="[&>button]:rounded-lg [&>button]:border-[rgb(var(--surface-border))] [&>button]:bg-[rgb(var(--background))] [&>button]:px-4 [&>button]:py-3 [&>button]:text-[rgb(var(--foreground))]"
          />
          {fieldErrors?.topic?.[0] && (
            <p className="mt-1 text-xs text-[rgb(var(--error))]">{fieldErrors.topic[0]}</p>
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="message"
          className="mb-2 block text-sm font-medium text-[rgb(var(--foreground))]"
        >
          Message
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={6}
          className={cn(inputClassName, 'resize-y min-h-[140px]')}
          aria-invalid={Boolean(fieldErrors?.message)}
        />
        {fieldErrors?.message?.[0] && (
          <p className="mt-1 text-xs text-[rgb(var(--error))]">{fieldErrors.message[0]}</p>
        )}
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        isLoading={isPending}
        className="w-full sm:w-auto bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] hover:from-[#7C3AED] hover:via-[#DB2777] hover:to-[#D97706] text-white border-0"
      >
        Send message
      </Button>
    </form>
  );
}
